import { Suspense, useState, useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { captureTtclid } from "@/lib/tiktokTracking";
import { captureTwclid } from "@/lib/xAdsTracking";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { installRedesignClass } from "./redesign/flag";
import HomepageLegacy from "./pages/Homepage";
import AuthLegacy from "./pages/Auth";
import AuthCallbackLegacy from "./pages/AuthCallback";
import NotFoundLegacy from "./pages/NotFound";
import HomepageRedesign from "./redesign/pages/marketing/HomepageRedesign";
import NotFoundRedesign from "./redesign/pages/marketing/NotFoundRedesign";
import AuthRedesign from "./redesign/pages/auth/AuthRedesign";
import AuthCallbackRedesign from "./redesign/pages/auth/AuthCallbackRedesign";
import { pickPage } from "./redesign/pickPage";
import { MarketingRoute } from "./redesign/MarketingRoute";
import { EmailVerificationGuard } from "./components/EmailVerificationGuard";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { ActingAsProvider } from "./contexts/ActingAsContext";
import { WhiteLabelThemeProvider } from "./contexts/WhiteLabelThemeContext";
import { AccountFilterProvider } from "./contexts/AccountFilterContext";

const Homepage = pickPage(HomepageLegacy, HomepageRedesign);
const NotFound = pickPage(NotFoundLegacy, NotFoundRedesign);
const Auth = pickPage(AuthLegacy, AuthRedesign);
const AuthCallback = pickPage(AuthCallbackLegacy, AuthCallbackRedesign);

// Lazy load non-critical routes for better LCP
const Pricing = lazyWithRetry(() => import("./pages/Pricing"), "Pricing");
const PricingRedesign = lazyWithRetry(() => import("./redesign/pages/marketing/PricingRedesign"), "PricingRedesign");
const Blog = lazyWithRetry(() => import("./pages/Blog"), "Blog");
const BlogRedesign = lazyWithRetry(() => import("./redesign/pages/marketing/BlogRedesign"), "BlogRedesign");
const Docs = lazyWithRetry(() => import("./pages/Docs"), "Docs");
const DocsRedesign = lazyWithRetry(() => import("./redesign/pages/marketing/DocsRedesign"), "DocsRedesign");
const Contact = lazyWithRetry(() => import("./pages/Contact"), "Contact");
const ContactRedesign = lazyWithRetry(() => import("./redesign/pages/marketing/ContactRedesign"), "ContactRedesign");
const CaseStudies = lazyWithRetry(() => import("./pages/CaseStudies"), "CaseStudies");
const CaseStudiesRedesign = lazyWithRetry(() => import("./redesign/pages/marketing/CaseStudiesRedesign"), "CaseStudiesRedesign");
const TikTokCallbackPage = lazyWithRetry(() => import("./pages/TikTokCallbackPage"), "TikTokCallbackPage");
const InstagramCallbackPage = lazyWithRetry(() => import("./pages/InstagramCallbackPage"), "InstagramCallbackPage");
const VerifyEmailLegacy = lazyWithRetry(() => import("./pages/VerifyEmail"), "VerifyEmail");
const VerifyEmailRedesign = lazyWithRetry(() => import("./redesign/pages/auth/VerifyEmailRedesign"), "VerifyEmailRedesign");
const VerifyEmail = pickPage(
  (props) => <VerifyEmailLegacy {...props} />,
  (props) => <VerifyEmailRedesign {...props} />
);
const ResetPasswordLegacy = lazyWithRetry(() => import("./pages/ResetPassword"), "ResetPassword");
const ResetPasswordRedesign = lazyWithRetry(() => import("./redesign/pages/auth/ResetPasswordRedesign"), "ResetPasswordRedesign");
const ResetPassword = pickPage(
  (props) => <ResetPasswordLegacy {...props} />,
  (props) => <ResetPasswordRedesign {...props} />
);
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"), "TermsOfService");
const TermsOfServiceRedesign = lazyWithRetry(() => import("./redesign/pages/marketing/TermsOfServiceRedesign"), "TermsOfServiceRedesign");
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"), "PrivacyPolicy");
const PrivacyPolicyRedesign = lazyWithRetry(() => import("./redesign/pages/marketing/PrivacyPolicyRedesign"), "PrivacyPolicyRedesign");
const DataDeletionInstructions = lazyWithRetry(() => import("./pages/DataDeletionInstructions"), "DataDeletionInstructions");
const DataDeletionInstructionsRedesign = lazyWithRetry(() => import("./redesign/pages/marketing/DataDeletionInstructionsRedesign"), "DataDeletionInstructionsRedesign");
const AcceptInvitationLegacy = lazyWithRetry(() => import("./pages/AcceptInvitation").then(m => ({ default: m.AcceptInvitation })), "AcceptInvitation");
const AcceptInvitationRedesign = lazyWithRetry(() => import("./redesign/pages/auth/AcceptInvitationRedesign"), "AcceptInvitationRedesign");
const AcceptInvitationSetupRedesign = lazyWithRetry(() => import("./redesign/pages/auth/AcceptInvitationSetupRedesign"), "AcceptInvitationSetupRedesign");
const AcceptInvitation = pickPage(
  (props) => <AcceptInvitationLegacy {...props} />,
  (props) => <AcceptInvitationRedesign {...props} />
);
const JoinCampaignViaLink = lazyWithRetry(() => import("./pages/JoinCampaignViaLink"), "JoinCampaignViaLink");
const JoinPartner = lazyWithRetry(() => import("./pages/JoinPartner"), "JoinPartner");
const ApplyRedirect = lazyWithRetry(() => import("./pages/ApplyRedirect"), "ApplyRedirect");
const BusinessLayoutLegacy = lazyWithRetry(() => import("./layouts/BusinessLayout").then(m => ({ default: m.BusinessLayout })), "BusinessLayout");
const BusinessLayoutRedesigned = lazyWithRetry(() => import("./redesign/pages/business/BusinessLayoutRedesigned"), "BusinessLayoutRedesigned");
const WhiteGloveGuard = lazyWithRetry(() => import("./redesign/pages/business/WhiteGloveGuard").then(m => ({ default: m.WhiteGloveGuard })), "WhiteGloveGuard");
const BusinessLayout = pickPage(
  (props: any) => <BusinessLayoutLegacy {...props} />,
  (props: any) => <BusinessLayoutRedesigned {...props} />
);
const BusinessOverview = lazyWithRetry(() => import("./pages/business/BusinessOverview").then(m => ({ default: m.BusinessOverview })), "BusinessOverview");
const CampaignCommunity = lazyWithRetry(() => import("./pages/business/CampaignCommunity"), "CampaignCommunity");
const CommunityWorkspaceRoute = lazyWithRetry(() => import("./redesign/pages/community/CommunityWorkspaceRoute"), "CommunityWorkspaceRoute");
const CampaignApplications = lazyWithRetry(() => import("./pages/business/CampaignApplications").then(m => ({ default: m.CampaignApplications })), "CampaignApplications");
const CreatorMarketplace = lazyWithRetry(() => import("./pages/business/CreatorMarketplace").then(m => ({ default: m.CreatorMarketplace })), "CreatorMarketplace");
const FinancePayouts = lazyWithRetry(() => import("./pages/business/FinancePayouts"), "FinancePayouts");
const Transactions = lazyWithRetry(() => import("./pages/business/Transactions").then(m => ({ default: m.Transactions })), "Transactions");
const Reports = lazyWithRetry(() => import("./pages/business/Reports").then(m => ({ default: m.Reports })), "Reports");
const BusinessProfile = lazyWithRetry(() => import("./pages/business/BusinessProfile").then(m => ({ default: m.BusinessProfile })), "BusinessProfile");
const ApiKeys = lazyWithRetry(() => import("./pages/business/ApiKeys").then(m => ({ default: m.ApiKeys })), "ApiKeys");
const ExternalPipelines = lazyWithRetry(() => import("./pages/business/ExternalPipelines").then(m => ({ default: m.ExternalPipelines })), "ExternalPipelines");
const AdminExternalPipelines = lazyWithRetry(() => import("./pages/admin/AdminExternalPipelines"), "AdminExternalPipelines");
const TeamRoles = lazyWithRetry(() => import("./pages/business/TeamRoles").then(m => ({ default: m.TeamRoles })), "TeamRoles");
const Integrations = lazyWithRetry(() => import("./pages/business/IntegrationsGate").then(m => ({ default: m.IntegrationsGate })), "IntegrationsGate");
const Subscription = lazyWithRetry(() => import("./pages/business/Subscription").then(m => ({ default: m.Subscription })), "Subscription");
const SubscriptionSuccess = lazyWithRetry(() => import("./pages/business/SubscriptionSuccess").then(m => ({ default: m.SubscriptionSuccess })), "SubscriptionSuccess");
const BusinessUpgrade = lazyWithRetry(() => import("./pages/business/BusinessUpgrade").then(m => ({ default: m.BusinessUpgrade })), "BusinessUpgrade");
const BusinessAnalytics = lazyWithRetry(() => import("./pages/business/BusinessAnalytics"), "BusinessAnalytics");
const AdsManager = lazyWithRetry(() => import("./pages/business/ads/AdsManager"), "AdsManager");
const CreatorLayoutLegacy = lazyWithRetry(() => import("./layouts/CreatorLayout").then(m => ({ default: m.CreatorLayout })), "CreatorLayout");
const CreatorLayoutRedesigned = lazyWithRetry(() => import("./redesign/pages/creator/CreatorLayoutRedesigned"), "CreatorLayoutRedesigned");
const CreatorLayout = pickPage(
  (props: any) => <CreatorLayoutLegacy {...props} />,
  (props: any) => <CreatorLayoutRedesigned {...props} />
);
const CreatorDashboard = lazyWithRetry(() => import("./pages/CreatorDashboard"), "CreatorDashboard");
const BrowseCampaigns = lazyWithRetry(() => import("./pages/BrowseCampaigns"), "BrowseCampaigns");
const ApplyToJoinCampaigns = lazyWithRetry(() => import("./pages/creator/ApplyToJoinCampaigns"), "ApplyToJoinCampaigns");
const ActiveCampaigns = lazyWithRetry(() => import("./pages/ActiveCampaigns"), "ActiveCampaigns");

const CreatorCampaigns = lazyWithRetry(() => import("./pages/CreatorCampaigns"), "CreatorCampaigns");
const CreatorInvitations = lazyWithRetry(() => import("./pages/creator/CreatorInvitations"), "CreatorInvitations");
const CreatorContent = lazyWithRetry(() => import("./pages/CreatorContent"), "CreatorContent");
const CreatorScheduleContent = lazyWithRetry(() => import("./pages/CreatorScheduleContent"), "CreatorScheduleContent");
const CreatorEarnings = lazyWithRetry(() => import("./pages/CreatorEarnings"), "CreatorEarnings");
const CreatorProfile = lazyWithRetry(() => import("./pages/CreatorProfile"), "CreatorProfile");
const CreatorNotifications = lazyWithRetry(() => import("./pages/CreatorNotifications"), "CreatorNotifications");
const ConnectAccounts = lazyWithRetry(() => import("./pages/creator/ConnectAccounts"), "ConnectAccounts");
const LaunchCampaign = lazyWithRetry(() => import("./pages/LaunchCampaign"), "LaunchCampaign");
const CampaignStudio = lazyWithRetry(() => import("./pages/business/CampaignStudio"), "CampaignStudio");
const MyCommunities = lazyWithRetry(() => import("./pages/business/MyCommunities"), "MyCommunities");
const ViewInsights = lazyWithRetry(() => import("./pages/ViewInsights"), "ViewInsights");
const EnhancedReviewerAdminLegacy = lazyWithRetry(() => import("./pages/EnhancedReviewerAdmin"), "EnhancedReviewerAdmin");
const EnhancedReviewerAdminRedesign = lazyWithRetry(() => import("./redesign/pages/admin/EnhancedReviewerAdminRedesign"), "EnhancedReviewerAdminRedesign");
const EnhancedReviewerAdmin = pickPage(
  (props: any) => <EnhancedReviewerAdminLegacy {...props} />,
  (props: any) => <EnhancedReviewerAdminRedesign {...props} />
);
const AdminDashboardLegacy = lazyWithRetry(() => import("./pages/admin/AdminDashboard"), "AdminDashboard");
const KickAppealsQueue = lazyWithRetry(() => import("./pages/admin/KickAppealsQueue"), "KickAppealsQueue");
const KickAppealDetail = lazyWithRetry(() => import("./pages/admin/KickAppealDetail"), "KickAppealDetail");
const NewPartner = lazyWithRetry(() => import("./pages/admin/NewPartner"), "NewPartner");
const PartnerDetail = lazyWithRetry(() => import("./pages/admin/PartnerDetail"), "PartnerDetail");
const AdminDashboardRedesign = lazyWithRetry(() => import("./redesign/pages/admin/AdminDashboardRedesign"), "AdminDashboardRedesign");
const AdminDashboard = pickPage(
  (props: any) => <AdminDashboardLegacy {...props} />,
  (props: any) => <AdminDashboardRedesign {...props} />
);
const AdminRoute = lazyWithRetry(() => import("./components/admin/AdminRoute").then(m => ({ default: m.AdminRoute })), "AdminRoute");
const CreatorAccounts = lazyWithRetry(() => import("./pages/business/CreatorAccounts"), "CreatorAccounts");
const BusinessOnboarding = lazyWithRetry(() => import("./pages/business/BusinessOnboarding"), "BusinessOnboarding");
const BusinessMilestones = lazyWithRetry(() => import("./pages/business/BusinessMilestones"), "BusinessMilestones");
const BusinessTasksCenter = lazyWithRetry(() => import("./pages/business/BusinessTasksCenter"), "BusinessTasksCenter");
const PayoutHistory = lazyWithRetry(() => import("./pages/business/PayoutHistory"), "PayoutHistory");
const BusinessMessages = lazyWithRetry(() => import("./pages/business/Messages"), "Messages");
const BusinessNotifications = lazyWithRetry(() => import("./pages/business/BusinessNotifications"), "BusinessNotifications");
const CreatorProfileView = lazyWithRetry(() => import("./pages/business/CreatorProfileView"), "CreatorProfileView");
const CreatorMessages = lazyWithRetry(() => import("./pages/creator/Messages"), "Messages");
const CommunityEarnings = lazyWithRetry(() => import("./pages/creator/CommunityEarnings"), "CommunityEarnings");
const CommunityDashboard = lazyWithRetry(() => import("./pages/creator/CommunityDashboard"), "CommunityDashboard");
const PaymentPreferences = lazyWithRetry(() => import("./pages/creator/PaymentPreferences"), "PaymentPreferences");
const CampaignDetails = lazyWithRetry(() => import("./pages/creator/CampaignDetails"), "CampaignDetails");
const CampaignInvitationDetail = lazyWithRetry(() => import("./pages/creator/CampaignInvitationDetail"), "CampaignInvitationDetail");
const KickAppeal = lazyWithRetry(() => import("./pages/creator/KickAppeal"), "KickAppeal");
const CreatorBadgesPage = lazyWithRetry(() => import("./pages/creator/Badges"), "CreatorBadgesPage");
const OnboardingLegacy = lazyWithRetry(() => import("./pages/Onboarding"), "Onboarding");
const OnboardingRedesign = lazyWithRetry(() => import("./redesign/pages/onboarding/OnboardingRedesign"), "OnboardingRedesign");
const Onboarding = pickPage(
  (props) => <OnboardingLegacy {...props} />,
  (props) => <OnboardingRedesign {...props} />
);
const AIAgent = lazyWithRetry(() => import("./pages/business/AIAgent"), "AIAgent");
const EditCampaignStorefront = lazyWithRetry(() => import("./pages/business/EditCampaignStorefront"), "EditCampaignStorefront");
const EditA2JStorefront = lazyWithRetry(() => import("./pages/business/EditA2JStorefront"), "EditA2JStorefront");
const LaunchCampaignRedirect = lazyWithRetry(() => import("./components/LaunchCampaignRedirect").then(m => ({ default: m.LaunchCampaignRedirect })), "LaunchCampaignRedirect");
// Phase 6 redesign preview (PR #49) — visible at /redesign-preview
const RedesignPreview = lazyWithRetry(() => import("./redesign/preview/RedesignPreview"), "RedesignPreview");
// Phase 6 app-shell preview (PR #50) — visible at /shell-preview
const ShellPreview = lazyWithRetry(() => import("./redesign/preview/ShellPreview"), "ShellPreview");


// Sensible defaults: cache responses for 60s before they go stale.
// Mutations call setQueryData / invalidateQueries explicitly when freshness matters,
// and disabling refetchOnWindowFocus avoids surprise refetches when the user briefly
// alt-tabs. Per-query overrides (e.g. notification counts) can still set their own staleTime.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,         // 1 minute fresh window
      gcTime: 5 * 60_000,        // keep in cache 5 minutes after unmount
      refetchOnWindowFocus: false,
      refetchOnMount: false,     // reuse fresh cache on route revisits
      retry: 1,
    },
  },
});

// Universal loading messages - for all routes before we know user type
const loadingMessages = [
  "Welcome to CreatorVault...",
  "Where creators and brands connect",
  "Building the future of creator partnerships",
  "Powering thousands of brand collaborations",
  "Your platform for authentic content creation",
  "Trusted by creators and brands worldwide",
  "Real performance. Real results. Real earnings.",
  "The smarter way to work with creators",
  "Join the creator economy revolution",
  "Connecting talent with opportunity"
];

// Premium loading fallback component - consistent brand styling with cycling messages
const PageLoader = () => {
  const [messageIndex, setMessageIndex] = useState(0);
  // After 20s, surface a manual reload CTA. Defends against stale-chunk
  // hangs, CDN edge failures, and any other condition that leaves us
  // suspended indefinitely — user can recover with one click instead of
  // being trapped on a spinner.
  const [showRecovery, setShowRecovery] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 3000);
    const timeout = setTimeout(() => setShowRecovery(true), 20_000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center">
      <div className="relative">
        <div className="w-12 h-12 border-4 border-brand/30 border-t-brand rounded-full animate-spin" />
        <div className="absolute inset-0 w-12 h-12 bg-brand/10 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
      </div>
      <p className="mt-6 text-sm text-muted-foreground text-center px-4 max-w-sm animate-fade-in">
        {loadingMessages[messageIndex]}
      </p>
      {showRecovery && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 text-xs text-muted-foreground underline hover:text-foreground"
        >
          Taking too long? Reload page
        </button>
      )}
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // If onboarding not completed, redirect to onboarding (except for onboarding route itself)
  if (profile && !profile.onboarding_completed && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  
  return <>{children}</>;
};

const BrowseCampaignsRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  // Marketplace refresh (Aug 30, 2026): public /browse-campaigns landing links
  // now route to the new /marketplace canonical.
  if (user) return <Navigate to="/creator/campaigns/marketplace" replace />;
  return <Navigate to="/auth?type=creator&redirect=/creator/campaigns/marketplace" replace />;
};

// ---------------------------------------------------------------------------
// Community Workspace section + legacy redirect helpers
// ---------------------------------------------------------------------------
const VALID_COMMUNITY_SECTIONS = [
  "home",
  "leaderboard",
  "videos",
  "materials",
  "payouts",
  "members",
  "chat",
  "review",
  "a2j-applicants",
  "insights",
  "performance",
  "accounts",
  "settings",
] as const;
type CommunitySectionParam = typeof VALID_COMMUNITY_SECTIONS[number];

const CommunityWorkspaceSectionRoute = ({ admin = false }: { admin?: boolean }) => {
  const { campaignId, section } = useParams<{ campaignId: string; section: string }>();
  const valid = VALID_COMMUNITY_SECTIONS.includes(section as CommunitySectionParam);
  if (!valid) {
    // Unknown section — redirect to community home for that surface.
    const base = `/c/${campaignId}${admin ? "/admin" : ""}`;
    return <Navigate to={base} replace />;
  }
  return (
    <CommunityWorkspaceRoute admin={admin} section={section as CommunitySectionParam} />
  );
};

/**
 * Redirects to `to` while preserving the inbound `?...` query string.
 * Used for legacy creator paths (e.g. /creator/campaigns/communities?campaignId=X)
 * that must keep the campaignId so ActiveCampaigns can auto-open the
 * Connect Account dialog after the approval notification deep-link.
 */
const PreserveSearchRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  const target = `${to}${location.search || ''}`;
  return <Navigate to={target} replace />;
};

/**
 * Legacy redirect for old /business/campaigns/:id/community and
 * /creator/campaigns/:id/community routes. Old links keep working but
 * land in the new Community Workspace tab. We preserve any ?tab= query
 * param by mapping it into the new section URL.
 */
// Safety-net redirect for any old link or in-flight Stripe Checkout Session
// that was created before we corrected the post-checkout success/cancel URL
// to the canonical /business/growth/ads path. Stripe Sessions are immutable
// after create, so a user paying through an older session can still land
// here — we forward to the real route AND preserve the entire query string
// so the ?ads_funded=1&session_id=... handler in AdsManager runs and the
// resume-after-funding flow continues to work.
const LegacyAdsRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/business/growth/ads${search}`} replace />;
};

const LegacyCommunityRedirect = ({ role }: { role: "business" | "creator" }) => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  const validTab = tab && VALID_COMMUNITY_SECTIONS.includes(tab as CommunitySectionParam) ? tab : null;
  const adminPrefix = role === "business" ? "/admin" : "";
  const sectionPath = validTab && validTab !== "home" ? `/${validTab}` : "";
  return <Navigate to={`/c/${id}${adminPrefix}${sectionPath}`} replace />;
};

const DashboardRoute = () => {
  const { user, profile, loading, isAdmin } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  // If we don't have a session at all, /auth is the right place.
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // CRITICAL: Check admin FIRST via has_role (user_roles.admin). This is
  // the ONLY source of truth for admin access. profiles.role='admin' is
  // treated as a legacy hint, not authorization.
  if (isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Business or creator profile roles route to their dashboards directly.
  if (profile?.role === 'business') {
    return <Navigate to="/business/dashboard" replace />;
  }
  if (profile?.role === 'creator' || profile?.role === 'demo') {
    return <Navigate to="/creator/dashboard" replace />;
  }

  // 🛡️ LOOP GUARD (fixes ACE / Jack Of All Trades incident, 2026-07-05):
  // NEVER send an authenticated user back to /auth. That created a
  // sign-in ping-pong: /auth → (session valid) → /dashboard →
  // (unknown/legacy role) → <Navigate to="/auth"/> → (session still
  // valid) → /dashboard … The toast said "Signed in successfully" but
  // the URL never left /auth. Two months of paying users were locked out.
  //
  // If the profile role is anything else (null, 'admin' without
  // user_roles.admin row, or a future legacy enum value), fall back to
  // the closest reasonable dashboard based on organization membership.
  if (profile?.organization_id) {
    return <Navigate to="/business/dashboard" replace />;
  }
  return <Navigate to="/creator/dashboard" replace />;
};

/**
 * Fires ttq.page() on every SPA route change so TikTok Pixel sees
 * page views for client-side navigations, not just the initial hard load.
 * Must be rendered INSIDE <BrowserRouter> for useLocation() to work.
 */
const TikTokPageViewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== 'undefined' && window.ttq) {
      try {
        window.ttq.page();
      } catch {
        // never break navigation on tracking errors
      }
    }
  }, [location.pathname, location.search]);
  return null;
};

const App = () => {
  // Capture TikTok ttclid from URL on app initialization
  useEffect(() => {
    captureTtclid();
    captureTwclid();
    // Defensive: re-assert the .redesign class on every mount in case any
    // route or third-party script ever stripped it. The redesign tokens depend
    // on this class being present on <html> for color/background variables.
    installRedesignClass();
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <TikTokPageViewTracker />
          <AuthProvider>
            <ActingAsProvider>
            <WhiteLabelThemeProvider>
            <AccountFilterProvider>
              <div className="min-h-screen bg-background">
                <RouteErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                  <Route path="/" element={<Homepage />} />

                  {/* Marketing aliases used in outbound brand campaigns. */}
                  <Route path="/brands" element={<Navigate to="/?mode=brand" replace />} />
                  <Route path="/for-brands" element={<Navigate to="/?mode=brand" replace />} />
                  <Route path="/business" element={<Navigate to="/?mode=brand" replace />} />

                  <Route path="/pricing" element={
                    <MarketingRoute
                      legacy={<><Navigation /><Pricing /><Footer /></>}
                      redesign={<PricingRedesign />}
                    />
                  } />
                  <Route path="/blog" element={
                    <MarketingRoute
                      legacy={<><Navigation /><Blog /><Footer /></>}
                      redesign={<BlogRedesign />}
                    />
                  } />
                  <Route path="/docs" element={
                    <MarketingRoute
                      legacy={<><Navigation /><Docs /><Footer /></>}
                      redesign={<DocsRedesign />}
                    />
                  } />
                  <Route path="/contact" element={
                    <MarketingRoute
                      legacy={<><Navigation /><Contact /><Footer /></>}
                      redesign={<ContactRedesign />}
                    />
                  } />
                  <Route path="/case-studies" element={
                    <MarketingRoute
                      legacy={<><Navigation /><CaseStudies /><Footer /></>}
                      redesign={<CaseStudiesRedesign />}
                    />
                  } />
                  <Route path="/terms-of-service" element={
                    <MarketingRoute
                      legacy={<><Navigation /><TermsOfService /><Footer /></>}
                      redesign={<TermsOfServiceRedesign />}
                    />
                  } />
                  <Route path="/privacy-policy" element={
                    <MarketingRoute
                      legacy={<><Navigation /><PrivacyPolicy /><Footer /></>}
                      redesign={<PrivacyPolicyRedesign />}
                    />
                  } />
                  <Route path="/data-deletion" element={
                    <MarketingRoute
                      legacy={<><Navigation /><DataDeletionInstructions /><Footer /></>}
                      redesign={<DataDeletionInstructionsRedesign />}
                    />
                  } />
                  <Route path="/browse-campaigns" element={<BrowseCampaignsRedirect />} />
                  <Route path="/join/:slug" element={<JoinPartner />} />
                  {/* Public A2J invitation gateway. Redirects auth'd creators
                      straight to the campaign storefront; sends unauth'd users
                      through /auth with ?redirect back to this path so email
                      invites survive signup + email verification. */}
                  <Route path="/apply/:campaignId" element={<ApplyRedirect />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/auth/verify-email" element={<VerifyEmail />} />
                  <Route path="/auth/reset-password" element={<ResetPassword />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/auth/tiktok/callback" element={<TikTokCallbackPage />} />
                  <Route path="/auth/instagram/callback" element={<InstagramCallbackPage />} />
                  <Route path="/onboarding" element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <EmailVerificationGuard>
                        <DashboardRoute />
                      </EmailVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/campaigns/new" element={
                    <ProtectedRoute>
                      <LaunchCampaign />
                    </ProtectedRoute>
                  } />
                  {/* Community Workspace — dedicated tab, sidebar layout, deep-linked sections.
                      Top-level (NOT inside Business/Creator shell) so it gets its own atmosphere. */}
                  <Route path="/c/:campaignId" element={
                    <ProtectedRoute>
                      <EmailVerificationGuard>
                        <CommunityWorkspaceRoute section="home" />
                      </EmailVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/c/:campaignId/admin" element={
                    <ProtectedRoute>
                      <EmailVerificationGuard>
                        <CommunityWorkspaceRoute admin section="home" />
                      </EmailVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/c/:campaignId/admin/:section" element={
                    <ProtectedRoute>
                      <EmailVerificationGuard>
                        <CommunityWorkspaceSectionRoute admin />
                      </EmailVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/c/:campaignId/:section" element={
                    <ProtectedRoute>
                      <EmailVerificationGuard>
                        <CommunityWorkspaceSectionRoute />
                      </EmailVerificationGuard>
                    </ProtectedRoute>
                  } />

                  <Route path="/launch-campaign" element={<LaunchCampaignRedirect />} />
                  <Route path="/business/insights" element={
                    <ProtectedRoute>
                      <ViewInsights />
                    </ProtectedRoute>
                  } />
                  
                  {/* Business Dashboard Routes */}
                  <Route path="/business/*" element={
                    <ProtectedRoute>
                      <EmailVerificationGuard>
                        <BusinessLayout />
                      </EmailVerificationGuard>
                    </ProtectedRoute>
                  }>
                    <Route path="dashboard" element={<BusinessOverview />} />
                    <Route path="campaigns/studio" element={<CampaignStudio />} />
                    <Route path="campaigns" element={<Navigate to="/business/campaigns/studio" replace />} />
                    <Route path="campaigns/new" element={<WhiteGloveGuard allowActingAs><LaunchCampaign /></WhiteGloveGuard>} />
                    <Route path="campaigns/:id/edit" element={<WhiteGloveGuard allowActingAs><LaunchCampaign editMode={true} /></WhiteGloveGuard>} />
                    <Route path="campaigns/:id/community" element={<LegacyCommunityRedirect role="business" />} />
                    <Route path="campaigns/:id/applications" element={<CampaignApplications />} />
                    <Route path="campaigns/:id/storefront" element={<EditCampaignStorefront />} />
                    {/* No WhiteGloveGuard — the A2J storefront is the managed
                        brand's own content. EditA2JStorefront gates on RLS-mirroring
                        access (direct owner / acting-as / WG operator) internally. */}
                    <Route path="campaigns/:id/a2j-storefront" element={<EditA2JStorefront />} />
                    <Route path="communities" element={<WhiteGloveGuard><MyCommunities /></WhiteGloveGuard>} />
                    <Route path="creators/accounts" element={<CreatorAccounts />} />
                    <Route path="creators/marketplace" element={<WhiteGloveGuard restrictedTitle="Your creator roster is curated" restrictedBody="Your account team hand-picks creators for each campaign. Head to Roster to see everyone active on your program, or message your team if you have someone specific in mind."><CreatorMarketplace /></WhiteGloveGuard>} />
                    <Route path="creators/profile/:creatorId" element={<ErrorBoundary><CreatorProfileView /></ErrorBoundary>} />
                    <Route path="messages" element={<BusinessMessages />} />
                    <Route path="notifications" element={<BusinessNotifications />} />
                    <Route path="finance/payouts" element={<FinancePayouts />} />
                    <Route path="finance/payouts/history" element={<PayoutHistory />} />
                    <Route path="finance/transactions" element={<WhiteGloveGuard restrictedTitle="Billing is handled for you" restrictedBody="Your account team manages transactions and invoicing behind the scenes. Head to Creator payouts for a read-only view of what's flowing out to creators."><Transactions /></WhiteGloveGuard>} />
                    {/* Reports page hidden */}
                    <Route path="health/onboarding" element={<WhiteGloveGuard><BusinessOnboarding /></WhiteGloveGuard>} />
                    <Route path="health/milestones" element={<WhiteGloveGuard><BusinessMilestones /></WhiteGloveGuard>} />
                    <Route path="health/tasks" element={<WhiteGloveGuard><BusinessTasksCenter /></WhiteGloveGuard>} />
                    <Route path="settings/profile" element={<BusinessProfile />} />
                    <Route path="settings/api-keys" element={<ApiKeys />} />
                    <Route path="settings/external-pipelines" element={<ExternalPipelines />} />
                    <Route path="settings/team" element={<TeamRoles />} />
                    <Route path="settings/integrations" element={<Integrations />} />
                    <Route path="settings/billing" element={<Navigate to="/business/settings/profile" replace />} />
                    <Route path="settings" element={<Navigate to="/business/settings/profile" replace />} />
                    <Route path="upgrade" element={<WhiteGloveGuard restrictedTitle="You're on our top tier" restrictedBody="Your program includes the full platform — there's nothing to upgrade. If you'd like to expand what your team runs for you, just message us."><BusinessUpgrade /></WhiteGloveGuard>} />
                    <Route path="analytics/platform" element={<BusinessAnalytics />} />
                    <Route path="growth/ads" element={<WhiteGloveGuard allowActingAs restrictedTitle="Ads are managed by your team" restrictedBody="Your account team runs paid amplification for your program. Message us if you want to spin up a new ad push or pull performance for a specific creator."><AdsManager /></WhiteGloveGuard>} />
                    <Route path="growth" element={<Navigate to="/business/growth/ads" replace />} />
                    {/* Safety net — see LegacyAdsRedirect comment. */}
                    <Route path="ads" element={<LegacyAdsRedirect />} />
                    <Route path="ai-agent" element={<WhiteGloveGuard><AIAgent /></WhiteGloveGuard>} />
                  </Route>

                  {/* Subscription success page - standalone without sidebar */}
                  <Route path="/business/subscription/success" element={
                    <ProtectedRoute>
                      <EmailVerificationGuard>
                        <SubscriptionSuccess />
                      </EmailVerificationGuard>
                    </ProtectedRoute>
                  } />

                  {/* Creator Dashboard Routes */}
                  <Route path="/creator/*" element={
                    <ProtectedRoute>
                      <EmailVerificationGuard>
                        <CreatorLayout />
                      </EmailVerificationGuard>
                    </ProtectedRoute>
                  }>
                    <Route index element={<DashboardRoute />} />
                    <Route path="dashboard" element={<CreatorDashboard />} />
                    {/* Specific campaign routes FIRST (more specific paths) */}
                    <Route path="campaigns/:id/community" element={<LegacyCommunityRedirect role="creator" />} />
                    <Route path="campaigns/communities/:communityId/earnings" element={<CommunityEarnings />} />
                    <Route path="campaigns/invitation/:invitationId" element={<CampaignInvitationDetail />} />
                    <Route path="kicks/:kickId/appeal" element={<KickAppeal />} />
                    <Route path="campaigns/:campaignId" element={<CampaignDetails />} />
                    
                    {/* Then the nested campaigns layout route */}
                    <Route path="campaigns" element={<CreatorCampaigns />}>
                      {/* Marketplace refresh (Aug 30, 2026): /marketplace is
                          the new canonical creator campaign feed — same UX as
                          the old Apply-to-Join page, retitled to "Marketplace".
                          Legacy /browse and /apply-to-join redirect to it. */}
                      <Route index element={<Navigate to="marketplace" replace />} />
                      <Route path="marketplace" element={<ApplyToJoinCampaigns />} />
                      <Route path="browse" element={<Navigate to="/creator/campaigns/marketplace" replace />} />
                      <Route path="apply-to-join" element={<Navigate to="/creator/campaigns/marketplace" replace />} />
                      <Route path="active" element={<ActiveCampaigns />} />
                      <Route path="invitations" element={<CreatorInvitations />} />
                    <Route path="history" element={<Navigate to="/creator/campaigns/active" replace />} />
                      {/* Legacy /communities path: redirect to /active but PRESERVE the
                          ?campaignId=... query string so the approval-notification
                          deep-link still auto-opens the Connect Account dialog. */}
                      <Route path="communities" element={<PreserveSearchRedirect to="/creator/campaigns/active" />} />
                    </Route>
                    <Route path="content/*" element={<CreatorContent />} />
                    <Route path="schedule-content" element={<CreatorScheduleContent />} />
                    <Route path="earnings/*" element={<CreatorEarnings />} />
                    <Route path="payment-preferences" element={<PaymentPreferences />} />
                    <Route path="profile/connect-accounts" element={<ConnectAccounts />} />
                    <Route path="profile/*" element={<CreatorProfile />} />
                    <Route path="messages" element={<CreatorMessages />} />
                    <Route path="notifications/*" element={<CreatorNotifications />} />
                    <Route path="badges" element={<CreatorBadgesPage />} />
                  </Route>
                  
                  {/* Admin Dashboard Route */}
                  <Route path="/admin/dashboard" element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminDashboard />
                      </AdminRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/admin/kick-appeals" element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <KickAppealsQueue />
                      </AdminRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/admin/kick-appeals/:appealId" element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <KickAppealDetail />
                      </AdminRoute>
                    </ProtectedRoute>
                  } />

                  {/* White-glove partner brands */}
                  <Route path="/admin/partners/new" element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <NewPartner />
                      </AdminRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/admin/partners/:orgId" element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <PartnerDetail />
                      </AdminRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/admin/external-pipelines" element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminExternalPipelines />
                      </AdminRoute>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/reviewer-admin" element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <EnhancedReviewerAdmin />
                      </AdminRoute>
                    </ProtectedRoute>
                  } />
                  
                  {/* Phase 6 redesign preview — kit page, no auth needed */}
                  <Route path="/redesign-preview" element={<RedesignPreview />} />
                  <Route path="/shell-preview" element={<ShellPreview />} />
                  
                  {/* Public invitation acceptance page */}
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />
                  <Route path="/accept-invitation/setup" element={<AcceptInvitationSetupRedesign />} />
                  
                  {/* Campaign invitation link page */}
                  <Route path="/join-campaign/:token" element={<JoinCampaignViaLink />} />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                </RouteErrorBoundary>
              </div>
            </AccountFilterProvider>
            </WhiteLabelThemeProvider>
            </ActingAsProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
  );
};

export default App;